/**
 * Interactive Media Module
 * 
 * A single video with time-based seeking for each zone's in/out animations.
 * Two interaction modes based on device capability:
 * - Touch devices: tap button[aria-expanded] to toggle panels
 * - Mouse devices: hover on parent <li> to show/hide panels
 * 
 * @see _docs/interactive-media-v2-context.md for full documentation
 */

export function interactiveMedia() {
  const modules = document.querySelectorAll('.interactive-media');
  if (!modules.length) return;

  /** @type {MediaQueryList} - Detects hover-capable devices */
  const hoverQuery = window.matchMedia('(hover: hover)');

  /** @type {MediaQueryList} - Detects reduced motion preference */
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @type {MediaQueryList} - Detects mobile viewport */
  const mobileQuery = window.matchMedia('(max-width: 767px)');

  /** @constant {number} - Debounce delay for hover events (ms) */
  const HOVER_DELAY = 150;

  /** @constant {number} - Scroll distance to trigger auto-close on mobile (px) */
  const SCROLL_THRESHOLD = 250;

  /** @constant {boolean} - Enable slow playback + timecode overlay for debugging */
  const DEBUG = false;

  /** @constant {number} - Playback rate when DEBUG is true (0.25 = quarter speed) */
  const DEBUG_PLAYBACK_RATE = 0.25;

  /**
   * Time ranges for each zone's animations (in seconds).
   * Video is 30fps: 15 frames "in" (0.5s), 4 frames "out" (~0.133s)
   * @type {Array<{in: [number, number], out: [number, number]}>}
   */
  const TIMINGS = [
    { in: [0, 0.5], out: [0.5, 0.633] },
    { in: [0.633, 1.133], out: [1.133, 1.267] },
    { in: [1.267, 1.767], out: [1.767, 1.9] },
  ];

  modules.forEach(module => {
    /** @type {HTMLLIElement[]} */
    const zones = [...module.querySelectorAll('.interactive-media-zone')];

    /** @type {HTMLVideoElement|null} */
    const video = module.querySelector('.interactive-media-video video');
    
    /** @type {number|null} - Currently open panel index, or null if none */
    let activeIndex = null;

    /** @type {number|null} - Timeout ID for hover debouncing */
    let hoverTimeout = null;

    /** @type {number} - Scroll position when panel opened (for scroll-to-close) */
    let scrollStartY = 0;

    /** @type {number[]} - Queue of zone indices waiting to open */
    let pendingQueue = [];

    /** @type {boolean} - True while a panel transition is in progress */
    let isProcessing = false;

    /** @type {boolean} - True once video fires canplaythrough */
    let videoReady = false;

    /**
     * Get the index of a zone element.
     * @param {HTMLLIElement} zone
     * @returns {number} Index in zones array, or -1 if not found
     */
    const getZoneIndex = (zone) => zones.indexOf(zone);

    /**
     * Get the trigger button and panel elements for a zone.
     * @param {HTMLLIElement} zone
     * @returns {{trigger: HTMLButtonElement|null, panel: HTMLElement|null}}
     */
    const getZoneParts = (zone) => {
      const trigger = zone.querySelector('button[aria-expanded]');
      const panel = trigger?.nextElementSibling;
      return { trigger, panel };
    };

    /**
     * Play a segment of the video from start to end time.
     * Uses requestAnimationFrame for precise end detection.
     * Respects prefers-reduced-motion by seeking instantly.
     * @param {number} start - Start time in seconds
     * @param {number} end - End time in seconds
     * @returns {Promise<void>} Resolves when segment completes
     */
    const playSegment = (start, end) => {
      if (!video) return Promise.resolve();

      video.currentTime = start;

      if (motionQuery.matches) {
        video.currentTime = end;
        return Promise.resolve();
      }

      video.play();
      return new Promise(resolve => {
        const checkTime = () => {
          if (video.currentTime >= end) {
            video.pause();
            resolve();
          } else {
            requestAnimationFrame(checkTime);
          }
        };
        requestAnimationFrame(checkTime);
      });
    };

    /**
     * Handle scroll events for mobile scroll-to-close behavior.
     * Closes active panel if user scrolls beyond threshold.
     * Does not restore focus (prevents scroll jump).
     */
    const handleScroll = () => {
      if (activeIndex !== null && Math.abs(window.scrollY - scrollStartY) > SCROLL_THRESHOLD) {
        closePanel(activeIndex, { restoreFocus: false });
      }
    };

    /**
     * Close a panel by index.
     * Hides panel, updates aria-expanded, plays "out" video segment.
     * @param {number} index - Zone index to close
     * @param {Object} [options]
     * @param {boolean} [options.restoreFocus=true] - Whether to return focus to trigger
     * @returns {Promise<void>} Resolves when "out" segment completes
     */
    const closePanel = async (index, { restoreFocus = true } = {}) => {
      const zone = zones[index];
      if (!zone) return;

      const { trigger, panel } = getZoneParts(zone);
      if (!trigger || !panel) return;

      trigger.setAttribute('aria-expanded', 'false');
      panel.hidden = true;
      
      if (restoreFocus) {
        trigger.focus();
      } else {
        document.activeElement?.blur();
      }

      window.removeEventListener('scroll', handleScroll);

      if (activeIndex === index) activeIndex = null;

      const timing = TIMINGS[index];
      if (timing) await playSegment(timing.out[0], timing.out[1]);
    };

    /**
     * Open a panel by index.
     * Shows panel immediately, plays "in" video segment.
     * Queues request if another transition is in progress.
     * @param {number} index - Zone index to open
     * @returns {Promise<void>} Resolves when "in" segment completes
     */
    const openPanel = async (index) => {
      if (!videoReady || activeIndex === index) return;

      // Queue if currently processing another panel
      if (isProcessing) {
        if (pendingQueue[pendingQueue.length - 1] !== index) {
          pendingQueue.push(index);
        }
        return;
      }

      isProcessing = true;

      // Close any currently open panel first (no focus restore in hover mode)
      if (activeIndex !== null) {
        await closePanel(activeIndex, { restoreFocus: !hoverQuery.matches });
      }

      const zone = zones[index];
      if (!zone) {
        isProcessing = false;
        return;
      }

      const { trigger, panel } = getZoneParts(zone);
      if (!trigger || !panel) {
        isProcessing = false;
        return;
      }

      activeIndex = index;

      // Show panel immediately (before video plays)
      trigger.setAttribute('aria-expanded', 'true');
      panel.hidden = false;
      panel.querySelector('button, a, [tabindex="0"]')?.focus();

      // Set up scroll-to-close on mobile
      if (mobileQuery.matches) {
        scrollStartY = window.scrollY;
        window.addEventListener('scroll', handleScroll, { passive: true });
      }

      // Play "in" segment
      const timing = TIMINGS[index];
      if (timing) await playSegment(timing.in[0], timing.in[1]);

      isProcessing = false;

      // Process next queued panel
      if (pendingQueue.length) {
        const next = pendingQueue.shift();
        openPanel(next);
      }
    };

    /**
     * Handle click events on the module.
     * Routes to close button handler or touch-mode trigger handler.
     * @param {MouseEvent} e
     */
    const handleClick = (e) => {
      // Close button inside panel
      const closeBtn = e.target.closest('.interactive-media-zone > div > div button');
      if (closeBtn) {
        const zone = closeBtn.closest('.interactive-media-zone');
        const index = getZoneIndex(zone);
        if (index !== -1 && activeIndex === index) {
          const { trigger } = getZoneParts(zone);
          closePanel(index);
          trigger?.focus();
        }
        return;
      }

      // Touch mode: trigger button toggles panel
      if (!hoverQuery.matches) {
        const trigger = e.target.closest('button[aria-expanded]');
        if (trigger) {
          const zone = trigger.closest('.interactive-media-zone');
          const index = getZoneIndex(zone);
          if (index === -1) return;

          if (trigger.getAttribute('aria-expanded') === 'true') {
            closePanel(index);
          } else {
            openPanel(index);
          }
        }
      }
    };

    /**
     * Handle mouseenter on a zone (hover mode only).
     * Opens panel after debounce delay.
     * @param {MouseEvent} e
     */
    const handleZoneEnter = (e) => {
      if (!hoverQuery.matches) return;
      clearTimeout(hoverTimeout);
      
      const index = getZoneIndex(e.currentTarget);
      if (index !== -1) {
        hoverTimeout = setTimeout(() => openPanel(index), HOVER_DELAY);
      }
    };

    /**
     * Handle mouseleave on a zone (hover mode only).
     * Closes panel after debounce delay.
     * @param {MouseEvent} e
     */
    const handleZoneLeave = (e) => {
      if (!hoverQuery.matches) return;
      clearTimeout(hoverTimeout);
      
      const index = getZoneIndex(e.currentTarget);
      if (index !== -1 && activeIndex === index) {
        hoverTimeout = setTimeout(() => closePanel(index, { restoreFocus: false }), HOVER_DELAY);
      }
    };

    // -------------------------------------------------------------------------
    // Initialization
    // -------------------------------------------------------------------------

    // Debug overlay: shows timecode and slows playback
    if (DEBUG && video) {
      const debugOverlay = document.createElement('div');
      debugOverlay.style.cssText = 'position:absolute;top:1rem;left:1rem;background:rgba(0,0,0,.8);color:#0f0;font:14px/1 monospace;padding:.5rem;z-index:9999;pointer-events:none';
      module.querySelector('.interactive-media-video').appendChild(debugOverlay);
      video.playbackRate = DEBUG_PLAYBACK_RATE;
      video.addEventListener('timeupdate', () => {
        debugOverlay.textContent = video.currentTime.toFixed(3) + 's';
      });
    }

    // Initialize video at frame 0, wait for ready state
    if (video) {
      video.currentTime = 0;

      if (video.readyState >= 4) {
        videoReady = true;
      } else {
        video.addEventListener('canplaythrough', () => { videoReady = true; }, { once: true });
        video.load();
      }
    }

    // Attach event listeners
    module.addEventListener('click', handleClick);
    zones.forEach(zone => {
      zone.addEventListener('mouseenter', handleZoneEnter);
      zone.addEventListener('mouseleave', handleZoneLeave);
    });

    // Close panel when mouse leaves module entirely (hover mode)
    module.addEventListener('mouseleave', () => {
      if (!hoverQuery.matches) return;
      clearTimeout(hoverTimeout);
      if (activeIndex !== null) {
        closePanel(activeIndex, { restoreFocus: false });
      }
    });
  });
}
